import React, { useEffect, useState } from 'react';
import Modal from '../common/modal';
import VerticalMultiSelect, { VerticalOption } from './vertical-multi-select';
import TagMultiSelect from './tag-multi-select';
import { ClientMultiSelectWithSearch } from './filter-controls/ClientMultiSelectWithSearch';
import { use2026TagGroups } from '../../hooks/useTagGroups';
import { useDomains } from '../../hooks/useClients';

export interface CreateCollectionFormValues {
  name: string;
  description: string;
  visibility: boolean;
  clientIds: number[];
  /** Optional labels for selected clients (used by duplicate to avoid #id chips). */
  selectedClients?: { id: number; name: string }[];
  domainId: number | null;
  verticalIds: number[];
  tagIds: number[];
}

type CreateCollectionModalMode = 'create' | 'duplicate';

interface CreateCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: CreateCollectionFormValues) => Promise<void> | void;
  isSubmitting?: boolean;
  verticals?: VerticalOption[];
  isLoadingVerticals?: boolean;
  mode?: CreateCollectionModalMode;
  initialValues?: Partial<CreateCollectionFormValues>;
  title?: string;
}

const defaultValues: CreateCollectionFormValues = {
  name: '',
  description: '',
  visibility: false,
  clientIds: [],
  domainId: null,
  verticalIds: [],
  tagIds: [],
};

const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  verticals = [],
  isLoadingVerticals = false,
  mode = 'create',
  initialValues,
  title,
}) => {
  const [formValues, setFormValues] = useState<CreateCollectionFormValues>({
    ...defaultValues,
    ...(initialValues ?? {}),
  });
  const { allTags, isLoading: isLoadingTags } = use2026TagGroups();
  const { data: domains } = useDomains();

  useEffect(() => {
    if (isOpen) {
      setFormValues({ ...defaultValues, ...(initialValues ?? {}) });
    }
    // We intentionally re-seed only when the modal opens; we don't want to clobber
    // edits in-flight if `initialValues` reference changes mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const updateField = <K extends keyof CreateCollectionFormValues>(key: K, value: CreateCollectionFormValues[K]) => {
    setFormValues((prev) => {
      const next = { ...prev, [key]: value };
      // Clients are mutually exclusive with domain/verticals
      if (key === 'clientIds' && (value as number[]).length > 0) {
        next.domainId = null;
        next.verticalIds = [];
      } else if (key === 'domainId' && value != null) {
        next.clientIds = [];
      } else if (key === 'verticalIds' && (value as number[]).length > 0) {
        next.clientIds = [];
      }
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formValues.name.trim() || isSubmitting) {
      return;
    }

    await onSubmit({
      ...formValues,
      name: formValues.name.trim(),
      description: formValues.description.trim(),
      clientIds: formValues.clientIds,
      domainId: formValues.domainId,
    });
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const isSubmitDisabled = !formValues.name.trim() || isSubmitting;
  const isDuplicate = mode === 'duplicate';
  const headingText = title ?? (isDuplicate ? 'Duplicate Collection' : 'Create Collection');
  const submitIdleText = isDuplicate ? 'Duplicate Collection' : 'Create Collection';
  const submitBusyText = isDuplicate ? 'Duplicating…' : 'Creating…';

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div
          className="inline-block w-full transform overflow-visible rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-lg sm:align-middle"
          onClick={(event) => event.stopPropagation()}
        >
          <form onSubmit={handleSubmit}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{headingText}</h3>
              <button
                type="button"
                className="text-blue-primary text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Close
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Name</label>
                <input
                  className="focus:ring-blue-primary mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  type="text"
                  value={formValues.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  placeholder="Collection name"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Description</label>
                <textarea
                  className="focus:ring-blue-primary mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  rows={3}
                  value={formValues.description}
                  onChange={(event) => updateField('description', event.target.value)}
                  placeholder="Optional description"
                  disabled={isSubmitting}
                />
              </div>
              <p className="text-xs text-gray-500">
                Clients are mutually exclusive with Domain and Verticals. Enabling one or more clients clears Domain and
                Verticals. Domain and Verticals can still be used together.
              </p>
              <ClientMultiSelectWithSearch
                label="Clients (Groups)"
                selectedClientIds={formValues.clientIds}
                selectedClients={formValues.selectedClients}
                onChange={(ids) => updateField('clientIds', ids)}
                placeholder="None (not client-scoped)"
                disabled={isSubmitting}
              />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Domain</label>
                <select
                  className="focus:ring-blue-primary mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  value={formValues.domainId ?? ''}
                  onChange={(e) => updateField('domainId', e.target.value ? parseInt(e.target.value, 10) : null)}
                  disabled={isSubmitting}
                >
                  <option value="">None (global)</option>
                  {domains?.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} (#{d.id})
                    </option>
                  ))}
                </select>
              </div>
              <VerticalMultiSelect
                label="Verticals"
                verticals={verticals}
                selectedVerticalIds={formValues.verticalIds}
                onChange={(nextIds) => updateField('verticalIds', nextIds)}
                disabled={isSubmitting}
                isLoading={isLoadingVerticals}
              />
              <TagMultiSelect
                label="Tags"
                tags={allTags}
                selectedTagIds={formValues.tagIds}
                onChange={(nextIds) => updateField('tagIds', nextIds)}
                disabled={isSubmitting}
                isLoading={isLoadingTags}
                placeholder="Select tags (optional)"
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSubmitDisabled}
              >
                {isSubmitting ? submitBusyText : submitIdleText}
              </button>
            </div>
          </form>
        </div>
      }
    />
  );
};

export default CreateCollectionModal;
